import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Typography, Stack, TextField, Alert,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import { admissionApplicationService } from '../../../services/admissions/admissionApplicationService';
import { ApplicationDocument } from '../../../types/admissions';

interface Props {
  open: boolean;
  applicationId: string;
  document: ApplicationDocument;
  /** The documents email is on — rejections are gathered and sent together. */
  emailOn?: boolean;
  onCancel: () => void;
  onRejected: () => Promise<void> | void;
}

/**
 * Reject-a-document dialog — reason-gated. The backend records the reason
 * on the document, which the family sees on their status page. Nothing is
 * emailed here: the Documents tab sends one email for all rejected
 * documents once staff are done.
 */

const DocumentRejectDialog: React.FC<Props> = ({ open, applicationId, document, emailOn, onCancel, onRejected }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const docId = (document as any)._id ?? document.key;

  const close = () => {
    if (busy) return;
    setReason('');
    onCancel();
  };

  const submit = async () => {
    const trimmed = reason.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      await admissionApplicationService.rejectDocument(applicationId, docId, trimmed);
      enqueueSnackbar('Document rejected', { variant: 'success' });
      await onRejected();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to reject document', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
      <DialogTitle component="div">
        <Typography variant="h6" fontWeight={700}>Reject document</Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <b>{document.fileName}</b> ({document.key})
          </Typography>
          <TextField
            label="Reason"
            placeholder="E.g. image unclear; wrong document; missing attestation"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            minRows={2}
            fullWidth
            required
            autoFocus
            helperText={emailOn
              ? 'Shown on the application page, and in the email when you send it.'
              : 'Shown on the parent-facing status link so they know what to resubmit.'}
          />
          <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary">
              {emailOn ? (
                <>Nothing is emailed yet. When you’ve finished reviewing, send one email listing every rejected document.</>
              ) : (
                <>Contact the parent out-of-band to request the corrected file.</>
              )}
            </Typography>
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={close} disabled={busy}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          disableElevation
          onClick={submit}
          disabled={busy || !reason.trim()}
        >
          {busy ? 'Rejecting…' : 'Reject document'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentRejectDialog;
